import React, { createContext, useState } from 'react';
import produce from 'immer';
import { SelectedOperation } from './types/SelectedOperation';
import { MonitoredSegment } from './types/AlertConfiguration';

type ProductAlertsProviderProps = {
  children: React.ReactElement | React.ReactElement[];
};

export type Filters = {
  showAll: boolean | null;
  createdBy: string | null;
  shared: boolean | null;
  favourite: boolean | null;
  createdStart: string | null;
  createdEnd: string | null;
  segmentName: string | null;
};
export type FiltersPayload = Omit<Filters, 'segmentName' | 'shared'>;

type ProductAlertsProviderState = {
  filters: Filters;
  forcedReload: object | null;
  selectedOperation: SelectedOperation | null;
  // TODO: `monitorableSegments` will be removed after updating API
  monitorableSegments: MonitoredSegment.Item[];
};

type ProductAlertsContextValue = ProductAlertsProviderState & {
  reloadSegmentList: () => void;
  setSelectedOperation: (value: SelectedOperation | null) => void;
  changeSearchBySegmentName: (payload: string | null) => void;
  changeFilter: (payload: FiltersPayload) => void;
  clearSearch: () => void;
  // TODO: `setMonitorableSegments` will be removed after updating API
  setMonitorableSegments: (items: MonitoredSegment.Item[]) => void;
};

const initialState: ProductAlertsProviderState = {
  filters: {
    showAll: null,
    createdBy: null,
    shared: null,
    favourite: null,
    createdStart: null,
    createdEnd: null,
    segmentName: null,
  },
  forcedReload: null,
  selectedOperation: null,
  // TODO: `monitorableSegments` will be removed after updating API
  monitorableSegments: []
};

export const ProductAlertsContext = createContext<ProductAlertsContextValue>({
  ...initialState,
  reloadSegmentList: () => {},
  setSelectedOperation: () => {},
  changeSearchBySegmentName: () => {},
  changeFilter: () => {},
  clearSearch: () => {},
  // TODO: `setMonitorableSegments` will be removed after updating API
  setMonitorableSegments: () => {}
});

export const ProductAlertsProvider: React.FC<ProductAlertsProviderProps> = ({ children }) => {
  const [state, setState] = useState(initialState);

  const value: ProductAlertsContextValue = {
    filters: state.filters,
    forcedReload: state.forcedReload,
    selectedOperation: state.selectedOperation,
    // TODO: `monitorableSegments` will be removed after updating API
    monitorableSegments: state.monitorableSegments,

    // TODO: `setMonitorableSegments` will be removed after updating API
    setMonitorableSegments: (items) => {
      setState((prevState) => ({ ...prevState, monitorableSegments: items }));
    },
    reloadSegmentList: () => {
      setState((prevState) => ({ ...prevState, forcedReload: {} }));
    },
    setSelectedOperation: (value: SelectedOperation | null) => {
      setState((prevState) => ({ ...prevState, selectedOperation: value }));
    },
    changeSearchBySegmentName: (payload: string | null) => {
      setState(
        produce((draft: ProductAlertsProviderState) => {
          draft.filters.segmentName = payload;
        })
      );
    },
    changeFilter: (payload: FiltersPayload) => {
      setState(
        produce((draft: ProductAlertsProviderState) => {
          draft.filters.createdBy = payload.createdBy;
          draft.filters.showAll = payload.showAll;
          draft.filters.favourite = payload.favourite;
          draft.filters.createdStart = payload.createdStart;
          draft.filters.createdEnd = payload.createdEnd;
        })
      );
    },
    clearSearch: () => {
      setState({ ...state, filters: initialState.filters });
    },
  };

  return <ProductAlertsContext.Provider value={value}>{children}</ProductAlertsContext.Provider>;
};
